import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextField,
  TextareaAutosize,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import * as React from 'react'
import { INode } from 'shared/types/tree'
import { PotentiallyHtml } from '../PotentiallyHtml'
import { EditableBodyView } from './BodyView'
import { LeafView } from './LeafView'
import { ActionMenu } from '../ActionMenu'
import {
  AssignableNodeTypes,
  NavigableNodeTypes,
  NodeType,
} from 'shared/types/ingestion'
import { addDesmos, addGeogebraLeaf } from './TreeActions'
import { openTab } from '../Helpers'
import { isInAssignableTree, openNode } from '../utility/TreeNavigation'
import { NodeIdMap } from '../version_mapping/utility'
import { StudentFacingToggle } from '../utility/StudentFacingToggle'
import { getQuestionType, isReferenceNode } from '../utility/TreeHelpers'
import { copyToClipboard } from '../../utility'
import { useToggleState } from 'shared/hooks'
import { NodeTypeSelect } from './utility/NodeTypeSelect'
import { retrieveIds, useUpdateTreeNode } from './TreeRequests'
import { NodeEditLang } from './LanguageAttribute/NodeEditLang'

function ReferenceNode({ node }: { node: INode }) {
  return (
    <div style={{ border: 'solid 1px', padding: 12 }}>
      {node.type.replace('reference:', '')}:{node.body?.ids?.length}
    </div>
  )
}

const canHaveDesmos: string[] = [NodeType.Note, NodeType.Question]

export function NodeView({
  node,
  onClick,
  isRoot = true,
  nodeActions,
  leafActions,
  depth,
  idMap,
}: {
  node: INode
  onClick: (nodeId: string) => void
  isRoot?: boolean
  nodeActions: { title: string; action: (nId: string) => void }[]
  leafActions: { title: string; action: (nId: string) => void }[]
  depth: number
  idMap: NodeIdMap
}) {
  const [asJson, setAsJson] = React.useState(false)
  const [currentNodeActions, setCurrentNodeActions] = React.useState<
    { title: string; action: () => void }[]
  >([])
  const [addDesmos, setAddDesmos] = React.useState(false)
  const [borderColor, setBorderColor] = React.useState('black')

  React.useEffect(() => {
    if (AssignableNodeTypes.includes(node.type as NodeType)) {
      setBorderColor('green')
    } else if (NavigableNodeTypes.includes(node.type as NodeType)) {
      setBorderColor('blue')
    }
  }, [node])

  const toggleJson = () => setAsJson(() => !asJson)

  const copyJson = () => {
    copyToClipboard(JSON.stringify(node))
  }

  React.useEffect(() => {
    const actions = nodeActions.map(({ title, action }) => {
      return {
        title,
        action: () => action(node.id),
      }
    })
    actions.push(
      {
        title: asJson ? 'View Details' : 'As JSON',
        action: toggleJson,
      },
      {
        title: 'Copy JSON',
        action: copyJson,
      },
    )
    if (canHaveDesmos.includes(node.type)) {
      actions.push({
        title: 'Add Desmos',
        action: () => setAddDesmos(true),
      })
    }
    if (node.nodes?.length > 1) {
      actions.push({
        title: 'Reorder Children',
        action: () =>
          openTab(`/node_reorder/${node.tree_id}:${node.version}:${node.id}`),
      })
      actions.push({
        title: 'Copy Children',
        action: () =>
          openTab(
            `/node_child_copy/${node.tree_id}:${node.version}:${node.id}`,
          ),
      })
    }
    setCurrentNodeActions(actions)
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [nodeActions, node, asJson])

  if (isReferenceNode(node)) {
    return <ReferenceNode node={node} />
  }
  function nodeClick() {
    onClick(node.id)
  }

  return (
    <div style={{ border: `solid 1px ${borderColor}`, padding: 12 }}>
      <Box display="flex" justifyContent="space-between">
        <div>
          <NodeTypeView node={node} />
          <TitleComponent node={node} />
          <UpdatedCreated node={node} />
          <Box>
            <Button disabled={isRoot} onClick={nodeClick}>
              ID: {node.id}
            </Button>
            <Button onClick={() => copyToClipboard(node.id)}>Copy</Button>
            <Button onClick={() => openNode(node, idMap)}>Kiddom</Button>
          </Box>
          {idMap && isInAssignableTree(node, idMap) && (
            <Box>
              <StudentFacingToggle node={node} />
            </Box>
          )}
        </div>
        <ActionMenu actions={currentNodeActions} />
      </Box>
      {addDesmos && (
        <Box padding={'10px'} border={'1px solid'}>
          <AddDesmos node={node} desmosAdded={nodeClick} />
        </Box>
      )}
      {asJson && <NodeJsonView node={node} />}
      {!asJson && (
        <NodeDetailsView
          node={node}
          onClick={onClick}
          nodeActions={nodeActions}
          leafActions={leafActions}
          depth={depth}
          idMap={idMap}
        />
      )}
    </div>
  )
}

function UpdatedCreated({ node: { updated_at, created_at } }: { node: INode }) {
  const [updatedAt, setUpdatedAt] = React.useState('')
  const [createdAt, setCreatedAt] = React.useState('')
  React.useEffect(() => {
    if (updated_at && updated_at !== created_at) {
      setUpdatedAt(updated_at)
    }
    if (created_at) {
      setCreatedAt(created_at)
    }
  }, [])
  return (
    <>
      {updatedAt && <Box>Updated: {updatedAt}</Box>}
      {createdAt && <Box>Created: {createdAt}</Box>}
    </>
  )
}

function TitleComponent({ node }: { node: INode }) {
  const updater = useUpdateTreeNode()
  const [editing, setEditing] = React.useState(false)
  const [nodeTitle, setNodeTitle] = React.useState(node.title)

  async function doIt() {
    const success = await updater({
      ...retrieveIds(node),
      title: nodeTitle,
    })
    if (success) {
      node.title = nodeTitle
    }
    setEditing(false)
  }

  if (editing) {
    return (
      <Box>
        <TextField
          label={'Title'}
          fullWidth={true}
          value={nodeTitle}
          onChange={({ target: { value } }) => setNodeTitle(value)}
        />
        <Button onClick={() => setEditing(false)}>x</Button>
        <Button onClick={doIt}>d</Button>
      </Box>
    )
  }
  return (
    <Box>
      Title: {node.title}{' '}
      <Button onClick={() => setEditing(!editing)}>Edit</Button>
    </Box>
  )
}

function AddDesmos({
  node,
  desmosAdded,
}: {
  node: INode
  desmosAdded: () => void
}) {
  const [isDesmos, setIsDesmos] = React.useState(true)
  const [geogebraId, setGeogebraId] = React.useState('')
  function onCommit() {
    if (isDesmos) {
      addDesmos(geogebraId, node).then(desmosAdded)
    } else {
      addGeogebraLeaf(geogebraId, node).then(desmosAdded)
    }
  }
  return (
    <Box>
      <Box>
        Add{' '}
        <Button onClick={() => setIsDesmos(!isDesmos)}>
          {isDesmos ? 'Desmos' : 'Geogebra'}
        </Button>
      </Box>
      <Box>
        <TextField
          value={geogebraId}
          onChange={(e) => setGeogebraId(e.target.value)}
        ></TextField>
      </Box>
      <Box>
        <Button disabled={!geogebraId} onClick={onCommit}>
          Do It ({isDesmos ? 'Desmos' : 'Geogebra'})
        </Button>
      </Box>
    </Box>
  )
}

function NodeJsonView({ node }: { node: INode }) {
  const [truncate, setTruncate] = React.useState(true)
  const [isTruncated, setIsTruncated] = React.useState(false)
  const [asJson, setAsJson] = React.useState('')

  React.useEffect(() => {
    const showChildren = [NodeType.Question, NodeType.Presentation].includes(
      node.type as NodeType,
    )
    const nodes = showChildren ? node.nodes : []
    const truncated = { ...node, nodes }
    const limit = 250
    const isTrunc = truncated.body?.value?.length > limit
    setIsTruncated(isTrunc)
    if (truncate && isTrunc) {
      truncated.body = {
        ...truncated.body,
        value: truncated.body!.value.slice(0, limit) + '...',
      }
    }
    const jsonString = JSON.stringify(truncated, null, 2)
    setAsJson(jsonString)
  }, [node, truncate])

  return (
    <Box>
      {isTruncated && (
        <Button onClick={() => setTruncate(!truncate)}>Truncate</Button>
      )}
      <TextareaAutosize cols={115} disabled={true} value={asJson} />
    </Box>
  )
}

function NodeDetailsView({
  node,
  onClick,
  nodeActions,
  leafActions,
  depth,
  idMap,
}: {
  node: INode
  onClick: (nodeId: string) => void
  nodeActions: { title: string; action: (nId: string) => void }[]
  leafActions: { title: string; action: (nId: string) => void }[]
  depth: number
  idMap: NodeIdMap
}) {
  const [descriptionAsString, setDescriptionAsString] = React.useState(false)

  return (
    <React.Fragment>
      {!!node.description && (
        <div>
          <Button onClick={() => setDescriptionAsString(!descriptionAsString)}>
            String
          </Button>
          Description:{' '}
          {descriptionAsString ? (
            <p>{node.description}</p>
          ) : (
            <PotentiallyHtml s={node.description} />
          )}
        </div>
      )}
      {node.body && (
        <div style={{ border: 'solid 1px', padding: 5 }}>
          <EditableBodyView node={node} />
        </div>
      )}
      <Accordion>
        <AccordionSummary>Lang</AccordionSummary>
        <AccordionDetails>
          <NodeEditLang
            treeId={node.tree_id}
            versionId={node.version}
            nodeId={node.id}
          />
        </AccordionDetails>
      </Accordion>
      {!!node.leaves?.length && (
        <Accordion key={`leaves:${node.id}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {node.leaves?.length} Leaves
          </AccordionSummary>
          <AccordionDetails>
            {node.leaves?.map((leaf) => {
              return (
                <LeafView
                  key={leaf.id}
                  leaf={leaf}
                  leafActions={leafActions}
                  parentNode={node}
                />
              )
            })}
          </AccordionDetails>
        </Accordion>
      )}
      {!!node.nodes?.length && (
        <Accordion key={`nodes:${node.id}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {node.nodes.length} Nodes
          </AccordionSummary>
          <AccordionDetails>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingRight: '1rem',
              }}
            >
              {depth < 3 ? (
                node.nodes?.map((child) => (
                  <NodeView
                    key={child.id}
                    node={child}
                    onClick={onClick}
                    isRoot={false}
                    nodeActions={nodeActions}
                    leafActions={leafActions}
                    depth={depth + 1}
                    idMap={idMap}
                  />
                ))
              ) : (
                <div>
                  <Button onClick={() => onClick(node.id)}>
                    Click here to explore deeper
                  </Button>
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      )}
    </React.Fragment>
  )
}

function NodeTypeView({ node }: { node: INode }) {
  const updater = useUpdateTreeNode()
  const [editing, toggleEditing] = useToggleState(false)
  const onSelect = async (type: string) => {
    const success = await updater({
      ...retrieveIds(node),
      type,
    })
    if (success) {
      node.type = type
    }
    toggleEditing()
  }
  return (
    <Box>
      Type: {node.type}
      {node.type === NodeType.Question && `: ${getQuestionType(node)}`}
      <Button onClick={() => toggleEditing()}>Edit</Button>
      {editing && <NodeTypeSelect type={node.type} onSelect={onSelect} />}
    </Box>
  )
}
